import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { 
  Leaf, ArrowLeft, Trophy, Star, Flame, Target, 
  Award, Crown, Loader2, TrendingUp, Zap, BookOpen,
  Activity, Share2, Bell, BarChart2, Compass, Medal
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_ICONS = {
  'compass': Compass,
  'target': Target,
  'book-open': BookOpen,
  'flame': Flame,
  'trophy': Trophy,
  'activity': Activity,
  'share-2': Share2,
  'bell': Bell,
  'bar-chart-2': BarChart2,
  'crown': Crown,
};

const LEVEL_COLORS = {
  1: 'from-gray-400 to-gray-500',
  2: 'from-green-400 to-green-600',
  3: 'from-blue-400 to-blue-600',
  4: 'from-purple-400 to-purple-600',
  5: 'from-amber-400 to-amber-600',
  6: 'from-rose-400 to-rose-600',
  7: 'from-yellow-400 to-yellow-600',
};

const ACTION_LABELS = {
  'complete_assessment': 'Completed Assessment',
  'daily_journal': 'Journal Entry',
  'daily_symptom_log': 'Symptom Log',
  'set_reminder': 'Set Reminder',
  'share_with_provider': 'Shared with Provider',
  'view_analytics': 'Viewed Analytics',
  'streak_bonus_7': '7-Day Streak Bonus',
  'streak_bonus_30': '30-Day Streak Bonus',
};

export default function GamificationPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/rewards');
    }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const [profileRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/gamification/profile`, { headers: getAuthHeader() }),
        axios.get(`${API}/gamification/leaderboard?limit=10`)
      ]);
      
      setProfile(profileRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching gamification data:', error);
      toast.error('Failed to load rewards data');
    } finally {
      setIsLoading(false);
    }
  }, [user, getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const earnedBadges = profile?.badges?.filter(b => b.earned) || [];
  const unearnedBadges = profile?.badges?.filter(b => !b.earned) || [];

  const progressToNextLevel = profile ? 
    ((profile.total_points % 100) / (profile.points_to_next_level + (profile.total_points % 100))) * 100 : 0;

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Hero Section */}
        <div className={`bg-gradient-to-r ${LEVEL_COLORS[profile?.level || 1]} rounded-2xl p-8 mb-8 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm mb-1">Level {profile?.level || 1}</p>
              <h1 className="text-3xl font-bold mb-2">{profile?.level_name || 'Newcomer'}</h1>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                <span className="text-xl font-semibold">{profile?.total_points || 0} points</span>
              </div>
            </div>
            <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center">
              <Trophy className="w-12 h-12" />
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span>Progress to Level {(profile?.level || 1) + 1}</span>
              <span>{profile?.points_to_next_level || 0} pts needed</span>
            </div>
            <div className="h-3 bg-white/30 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-white rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(progressToNextLevel, 100)}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit mb-6">
          {['profile', 'badges', 'leaderboard'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
                activeTab === tab ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-border p-4 text-center">
                <Award className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold">{earnedBadges.length}</p>
                <p className="text-sm text-muted-foreground">Badges Earned</p>
              </div>
              <div className="bg-white rounded-xl border border-border p-4 text-center">
                <Target className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{unearnedBadges.length}</p>
                <p className="text-sm text-muted-foreground">Badges to Earn</p>
              </div>
              <div className="bg-white rounded-xl border border-border p-4 text-center">
                <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{profile?.level || 1}</p>
                <p className="text-sm text-muted-foreground">Current Level</p>
              </div>
              <div className="bg-white rounded-xl border border-border p-4 text-center">
                <Zap className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{profile?.recent_activity?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Recent Activities</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Recent Activity
              </h2>
              
              {profile?.recent_activity?.length > 0 ? (
                <div className="space-y-3">
                  {profile.recent_activity.map((activity, i) => (
                    <div key={activity.id || i} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Star className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{ACTION_LABELS[activity.action] || activity.action}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(activity.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-green-600">+{activity.points} pts</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No recent activity</p>
                  <p className="text-sm mt-1">Complete actions to earn points!</p>
                </div>
              )}
            </div>

            {/* How to Earn Points */}
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl border border-primary/20 p-6">
              <h2 className="font-semibold text-lg mb-4">How to Earn Points</h2>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-center gap-3 p-3 bg-white/50 rounded-lg">
                  <span className="text-primary font-bold">+50</span>
                  <span className="text-sm">Complete a health assessment</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/50 rounded-lg">
                  <span className="text-primary font-bold">+10</span>
                  <span className="text-sm">Log your daily supplements</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/50 rounded-lg">
                  <span className="text-primary font-bold">+10</span>
                  <span className="text-sm">Track your symptoms</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/50 rounded-lg">
                  <span className="text-primary font-bold">+25</span>
                  <span className="text-sm">Share with healthcare provider</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/50 rounded-lg">
                  <span className="text-primary font-bold">+15</span>
                  <span className="text-sm">Set up a reminder</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/50 rounded-lg">
                  <span className="text-primary font-bold">+50</span>
                  <span className="text-sm">7-day journaling streak</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'badges' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Earned Badges */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                Earned Badges ({earnedBadges.length})
              </h2>
              
              {earnedBadges.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {earnedBadges.map(badge => {
                    const IconComponent = BADGE_ICONS[badge.icon] || Award;
                    return (
                      <div 
                        key={badge.id}
                        className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border border-primary/20 text-center"
                      >
                        <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                          <IconComponent className="w-6 h-6 text-primary" />
                        </div>
                        <p className="font-semibold text-sm">{badge.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                        <p className="text-xs text-primary mt-2">
                          Earned {new Date(badge.earned_at).toLocaleDateString()}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Award className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No badges earned yet</p>
                  <p className="text-sm mt-1">Complete actions to unlock badges!</p>
                </div>
              )}
            </div>

            {/* Badges to Unlock */}
            <div className="bg-white rounded-2xl border border-border p-6">
              <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-500" />
                Badges to Unlock ({unearnedBadges.length})
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {unearnedBadges.map(badge => {
                  const IconComponent = BADGE_ICONS[badge.icon] || Award;
                  return (
                    <div 
                      key={badge.id}
                      className="p-4 bg-muted/50 rounded-xl border border-border text-center opacity-75"
                    >
                      <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                        <IconComponent className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <p className="font-semibold text-sm text-muted-foreground">{badge.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'leaderboard' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="bg-white rounded-2xl border border-border p-6">
              <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                Top Health Champions
              </h2>
              
              {leaderboard.length > 0 ? (
                <div className="space-y-3">
                  {leaderboard.map((entry, i) => (
                    <div 
                      key={i}
                      className={`flex items-center justify-between p-4 rounded-xl ${
                        i === 0 ? 'bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200' :
                        i === 1 ? 'bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200' :
                        i === 2 ? 'bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200' :
                        'bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          i === 0 ? 'bg-amber-500 text-white' :
                          i === 1 ? 'bg-gray-400 text-white' :
                          i === 2 ? 'bg-orange-400 text-white' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {i < 3 ? <Medal className="w-5 h-5" /> : entry.rank}
                        </div>
                        <div>
                          <p className="font-medium">{entry.name}</p>
                          <p className="text-xs text-muted-foreground">Level {entry.level} - {entry.level_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">{entry.total_points}</p>
                        <p className="text-xs text-muted-foreground">points</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No leaderboard data yet</p>
                  <p className="text-sm mt-1">Be the first to earn points!</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
